import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../components/Notifications";
import { archiveThread, updateThreadSubject } from "../../../services/MessageService";
import { formatTime } from "../../../utils/FormatUtils";
import { CircleButton } from "../../../components/CircleButton";
import { Show } from "../../../components/Show";
import { Hide } from "../../../components/Hide";
import { Menu } from "../../../components/Menu";
import { MenuItem } from "../../../components/MenuItem";
import { Recipients } from "./Recipients";

export function ThreadRow ({ thread, handleClick, setThreads, reloadThreads, showArchived }) {

  const [ isEditing, setIsEditing ] = useState(false);
  const [ editedSubject, setEditedSubject ] = useState(thread.subject);
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const subjectInputRef = useRef(null);

  const handleDelete = async (e) => {
    e.stopPropagation();
    addNotification('Delete functionality not implemented', 'warn');
  };

  const handleEdit = (e) => {
    setIsEditing(true);
    setTimeout(() => {
      subjectInputRef.current?.focus();
    }, 0);
  };

  const handleInvite = () => {
    navigate('/messages/participants/' + thread.id)
  };

  const handleArchive = async (e) => {
    e.stopPropagation();
    try {
      await archiveThread(thread.id, !showArchived);
      addNotification(`Successfully ${showArchived ? 'Unarchived' : 'Archived'}`, 'success');
      reloadThreads();
    } catch (err) {
      addNotification('Error archiving thread ' + err.message, 'error')
    }
  };

  const handleSubjectInputKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit(e);
    }
  }

  const handleSaveEdit = async (e) => {
    e.stopPropagation();
    if (editedSubject.trim() === '') {
      addNotification('Subject cannot be empty', 'error');
      return;
    }
    setThreads((prevThreads) =>
      prevThreads.map((t) =>
        t.id === thread.id ? { ...t, subject: editedSubject } : t
      )
    );
    try {
      await updateThreadSubject(thread.id, editedSubject);
      addNotification('Subject updated successfully', 'success');
    } catch (err) {
      addNotification('Error updating thread ' + err.message, 'error');
    }
    setIsEditing(false);
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditedSubject(thread.subject);
    setIsEditing(false);
  };

  const onRowClick = (e) => {
    if (!isEditing) {
      e.stopPropagation();
      handleClick();
    }
  }

  return (
    <div
      onClick={onRowClick}
      className="flex items-center gap-4 p-3 border-b border-gray-200 cursor-pointer select-none hover:bg-indigo-50"
      role="listitem"
    >
      <div className="flex-1 min-w-0 ml-4">
        <Show show={isEditing}>
          <div className="flex items-center gap-2">
            <input
              ref={subjectInputRef}
              type="text"
              value={editedSubject}
              onChange={(e) => setEditedSubject(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleSubjectInputKeyDown}
              placeholder="Enter subject"
              className="block w-5/6 text-sm font-semibold text-slate-800 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Edit thread subject"
            />
            <CircleButton 
              clickHandler={handleSaveEdit}
              icon='fa-check'
              tooltip="Save Subject"
            />
            <CircleButton 
              clickHandler={handleCancelEdit}
              icon='fa-times'
              tooltip="Cancel Edit"
            />
          </div>
        </Show>
        <Hide hide={isEditing}>
          <div className="flex items-center gap-2">
            <span className="block font-semibold text-slate-700 max-w-s">{thread.subject}</span>
          </div>
        </Hide>
        <Recipients recipients={thread.recipients}/>
      </div>

      <div className="text-sm text-slate-500 whitespace-nowrap ml-8 flex-shrink-0">
        <span>{formatTime(thread.createdTs)} <br/> {thread.messagesCount} messages</span>
      </div>
      
      <div className="relative p-0 m-0">
        <Menu>
          <MenuItem 
            clickHandler={handleArchive}
            icon='fa-box-archive text-indigo-500 text-gray-700 hover:bg-indigo-50'
            text={showArchived ? 'Unarchive' : 'Archive'}
            tooltip={(showArchived ? 'Unarchive' : 'Archive') + 'Thread'}
          />
          <MenuItem 
            show={!showArchived} 
            clickHandler={(e) => {e.stopPropagation(); handleInvite()}}
            icon='fa-user-plus text-indigo-500 text-gray-700 hover:bg-indigo-50'
            text='Participants'
            tooltip='Manage participants'
          />
          <MenuItem 
            show={!showArchived} 
            clickHandler={handleEdit}
            icon='fa-pencil text-indigo-500 text-gray-700 hover:bg-indigo-50'
            text='Edit'
            tooltip='Edit Thread'
          />
          <MenuItem 
            show={!showArchived} 
            clickHandler={handleDelete}
            icon='fa-trash text-red-800 text-gray-700 hover:bg-indigo-50'
            text='Delete'
            tooltip='Delete Thread'
          />
        </Menu>
      </div>

    </div>
  );
};
